import React from 'react';
import { cn } from '@/lib/utils';
import { TablePagination, PaginationState } from './TablePagination';
import GentlyTable, { TableColumn, SummaryRow } from './GentlyTable';

// Re-export types from GentlyTable for backward compatibility
export type { SortDirection, TableColumn, SummaryRow } from './GentlyTable';

export interface PaginationConfig {
  enabled: boolean;
  pagination: PaginationState;
  onPageSizeChange: (pageSize: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onFirstPage?: () => void;
  onLastPage?: () => void;
  onGoToPage?: (page: number) => void;
  showRowsPerPage?: boolean;
  showPageInfo?: boolean;
  showPageNumbers?: boolean;
  rowsPerPageOptions?: number[];
}

export interface PaginatedGentlyTableProps<T> {
  data: T[] | undefined;
  columns: TableColumn<T>[];
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  searchFields?: (keyof T)[];
  showSearch?: boolean;
  actions?: React.ReactNode;
  summaryRow?: SummaryRow;
  isLoading?: boolean;
  className?: string;
  showDownload?: boolean;
  downloadFilename?: string;
  showCount?: boolean; // Show current count of displayed items
  itemName?: string; // Name of items being displayed (e.g., "companies", "orders")
  customCountDisplay?: React.ReactNode; // Custom count display component

  // Scrollable table options
  maxHeight?: number | string; // Max height for table body (e.g., 600 or "600px")
  stickyHeader?: boolean; // Whether to make the header sticky when scrolling

  // Pagination props
  paginationConfig?: PaginationConfig;

  // Server-side features
  serverSideSearch?: {
    value: string;
    onChange: (value: string) => void;
    debounceMs?: number;
  };
  serverSideSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (columnId: string, direction: 'asc' | 'desc') => void;
  };
  defaultSort?: {
    sortBy: keyof T;
    sortDirection: 'asc' | 'desc';
  };
}

/**
 * A shadcn-based table component with pagination support.
 * Supports both client-side and server-side search, sorting, and pagination.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PaginatedGentlyTable<T extends Record<string, any>>({
  data,
  columns,
  searchPlaceholder = 'Search...',
  onRowClick,
  emptyMessage = 'No data found',
  searchFields = [],
  showSearch = true,
  actions,
  summaryRow,
  isLoading = false,
  className,
  showDownload = true,
  downloadFilename = 'table-data',
  showCount = false,
  itemName = 'items',
  customCountDisplay,
  maxHeight,
  stickyHeader,
  paginationConfig,
  serverSideSearch,
  serverSideSorting,
  defaultSort,
}: PaginatedGentlyTableProps<T>) {
  // Determine if client-side sorting should be disabled
  // Disable when pagination is enabled or server-side sorting is used
  const disableClientSideSort =
    paginationConfig?.enabled || !!serverSideSorting;

  return (
    <div className={cn('space-y-4', className)}>
      <div>
        <GentlyTable
          data={data}
          columns={columns}
          searchPlaceholder={searchPlaceholder}
          onRowClick={onRowClick}
          emptyMessage={emptyMessage}
          searchFields={searchFields}
          showSearch={showSearch}
          actions={actions}
          summaryRow={summaryRow}
          isLoading={isLoading}
          showDownload={showDownload}
          downloadFilename={downloadFilename}
          showCount={showCount}
          maxHeight={maxHeight}
          stickyHeader={stickyHeader}
          customCountDisplay={
            customCountDisplay !== undefined ? (
              customCountDisplay
            ) : showCount &&
              paginationConfig?.pagination.totalItems !== undefined ? (
              <div className="text-sm text-muted-foreground">
                {paginationConfig.pagination.totalItems === 0
                  ? `0 ${itemName}`
                  : `${paginationConfig.pagination.startItem}–${paginationConfig.pagination.endItem} of ${paginationConfig.pagination.totalItems} ${itemName}`}
              </div>
            ) : undefined
          }
          serverSideSearch={serverSideSearch}
          serverSideSorting={serverSideSorting}
          disableClientSideSort={disableClientSideSort}
          downloadAllData={data} // For paginated tables, download all data
          defaultSort={defaultSort}
        />
      </div>

      {/* Pagination */}
      {paginationConfig?.enabled && <TablePagination {...paginationConfig} />}
    </div>
  );
}

export default PaginatedGentlyTable;
